import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, map } from 'rxjs';
import { ITableServerFilter } from 'src/app/common/interfaces/table/table-server-filter.inteface';
import { ITableServerResponseFilter } from 'src/app/common/interfaces/table/table-server-filter-response.interface';
import { IWebMessage } from 'src/app/common/interfaces/webmessage-interface';
import { IDecisionedNotificationCount } from '../../interfaces/transaction-dual-approval/decisioned-notification-count.interface';
import { IDecisionedNotificationCountByTransactionType } from '../../interfaces/transaction-dual-approval/decisioned-notification-count-by-transaction-type.interface';
import { IFedNowTransactionDualApprovalSummary } from '../../interfaces/transaction-dual-approval/dual-approval-summary.interface';
import { IDualApprovalDecision } from '../../interfaces/transaction-dual-approval/dual-approval-decision-request.interface';
import {
  IDualApprovalRequiredRequest,
  IDualApprovalRequiredResponse,
} from '../../interfaces/transaction-dual-approval/dual-approval-required.interface';
import { AbstractTransactionDualApprovalService } from '../abstraction/abstract.transaction-dual-approval.service';

/**
 * Mock implementation of {@link AbstractTransactionDualApprovalService}.
 * Returns used for FedNow Credit Transfer and RFP Transaction Dual Approval-related calls for offline/mock-mode development.
 */
@Injectable({
  providedIn: 'root',
})
export class MockTransactionDualApprovalService extends AbstractTransactionDualApprovalService {
  protected tableFilterJson = 'assets/mocks/fednow/dual-approval/tableFilter.json';
  protected getSummaryJson = 'assets/mocks/fednow/dual-approval/getSummary.json';
  private readonly decisionedNotificationCounts: Record<
    'CT' | 'RFP',
    Record<'Approved' | 'Denied', number>
  > = {
    CT: { Approved: 1, Denied: 0 },
    RFP: { Approved: 0, Denied: 1 },
  };

  constructor(private http: HttpClient) {
    super();
  }

  tableFilter(filter: ITableServerFilter): Promise<ITableServerResponseFilter> {
    const statusFilter = filter.extendedData?.find(f => f.key === 'ApprovalStatus');
    const transactionTypeFilter = filter.extendedData?.find(f => f.key === 'TransactionType');

    return lastValueFrom(
      this.http.get<ITableServerResponseFilter>(this.tableFilterJson).pipe(
        map(response => {
          let filtered = response.queryable;

          // Implement basic filtering logic based on the provided filters.
          // This allows the mock to somewhat mimic the behavior of the real API when changing the status filter.
          if (statusFilter?.value) {
            const statusMap: Record<string, string> = {
              Pending: 'Decision Pending',
              ProcessingApproval: 'Processing',
              Approved: 'Approved',
              Denied: 'Denied',
            };
            const mappedStatus = statusMap[statusFilter.value] ?? statusFilter.value;
            filtered = filtered.filter((item: any) => item.approvalStatus === mappedStatus);
          }

          if (transactionTypeFilter?.value) {
            const transactionTypeMap: Record<string, string> = {
              CT: 'Credit Transfer Send',
              RFP: 'RFP Decision',
            };
            const mappedTransactionType =
              transactionTypeMap[transactionTypeFilter.value] ?? transactionTypeFilter.value;
            filtered = filtered.filter(
              (item: any) => item.associatedTransactionType === mappedTransactionType
            );
          }

          if (filter.value) {
            const search = filter.value.toLowerCase();
            filtered = filtered.filter(
              (item: any) =>
                item.reviewedBy?.toLowerCase().includes(search) ||
                item.initiatedBy?.toLowerCase().includes(search) ||
                item.uniqueMessageId?.toLowerCase().includes(search) ||
                item.fedNowUniqueMessageId?.toLowerCase().includes(search) ||
                item.rtpUniqueMessageId?.toLowerCase().includes(search)
            );
          }

          return {
            ...response,
            queryable: filtered,
            rowCount: filtered.length,
          };
        })
      )
    );
  }

  tableExportCSV(filter: ITableServerFilter): Promise<Blob> {
    throw new Error('Method not implemented.');
  }

  tableExportExcel(filter: ITableServerFilter): Promise<any> {
    throw new Error('Method not implemented.');
  }

  getUnfinishedFedNowTransactionApprovalCount(options?: {
    skipLoading?: boolean;
  }): Promise<{ count: number }> {
    return Promise.resolve({ count: 4 });
  }

  getUnfinishedRtpTransactionApprovalCount(options?: {
    skipLoading?: boolean;
  }): Promise<{ count: number }> {
    return Promise.resolve({ count: 4 });
  }

  getPendingFedNowTransactionApprovalCount(options?: {
    skipLoading?: boolean;
  }): Promise<{ count: number }> {
    return Promise.resolve({ count: 2 });
  }

  getDecisionedNotificationCountsByStatus(options?: {
    skipLoading?: boolean;
    transactionType?: string;
  }): Promise<IDecisionedNotificationCount[]> {
    const transactionType = options?.transactionType === 'RFP' ? 'RFP' : 'CT';
    const counts = this.decisionedNotificationCounts[transactionType];

    return Promise.resolve(
      (['Approved', 'Denied'] as const)
        .filter(approvalStatus => counts[approvalStatus] > 0)
        .map(approvalStatus => ({
          approvalStatus,
          count: counts[approvalStatus],
        }))
    );
  }

  getDecisionedNotificationCountsByTransactionType(options?: {
    skipLoading?: boolean;
  }): Promise<IDecisionedNotificationCountByTransactionType[]> {
    return Promise.all([
      this.getDecisionedNotificationCountsByStatus({
        transactionType: 'CT',
        skipLoading: options?.skipLoading,
      }),
      this.getDecisionedNotificationCountsByStatus({
        transactionType: 'RFP',
        skipLoading: options?.skipLoading,
      }),
    ]).then(([ctCounts, rfpCounts]) => [
      ...ctCounts.map(count => ({
        transactionType: 'Credit Transfer Send' as const,
        ...count,
      })),
      ...rfpCounts.map(count => ({
        transactionType: 'RFP Decision' as const,
        ...count,
      })),
    ]);
  }

  clearDecisionedNotifications(options?: {
    skipLoading?: boolean;
    transactionType?: string;
  }): Promise<IWebMessage> {
    const transactionType = options?.transactionType === 'RFP' ? 'RFP' : 'CT';
    this.decisionedNotificationCounts[transactionType] = { Approved: 0, Denied: 0 };

    return Promise.resolve({ status: 'Success', message: '', object: null });
  }

  getSummary(id: number): Promise<IFedNowTransactionDualApprovalSummary> {
    return lastValueFrom(this.http.get<IFedNowTransactionDualApprovalSummary>(this.getSummaryJson));
  }

  approve(request: IDualApprovalDecision): Promise<IWebMessage> {
    return Promise.resolve({ status: 'Success', message: '', object: null });
  }

  deny(request: IDualApprovalDecision): Promise<IWebMessage> {
    return Promise.resolve({ status: 'Success', message: '', object: null });
  }

  rfpApprove(request: IDualApprovalDecision): Promise<IWebMessage> {
    return Promise.resolve({ status: 'Success', message: '', object: null });
  }

  rfpDeny(request: IDualApprovalDecision): Promise<IWebMessage> {
    return Promise.resolve({ status: 'Success', message: '', object: null });
  }

  noDecision(dualSummaryId: number): Promise<IWebMessage> {
    return Promise.resolve({ status: 'Success', message: '', object: null });
  }

  isDualApprovalRequired(
    request: IDualApprovalRequiredRequest
  ): Promise<IDualApprovalRequiredResponse> {
    return Promise.resolve({ isDualApprovalRequired: request.amount > 10000 });
  }
}
